import type { CollectionRef } from './collections';
import type { PersonRef } from './people';

export type InvitationKind = 'group' | 'personal';

/**
 * As the inviter sees it. There is no "declined": a declined invite reads
 * usable until it expires, so nobody learns that they were turned down.
 */
export type InvitationStatus = 'usable' | 'revoked' | 'expired' | 'usedUp';

/** `collection` is null once the offered collection has been deleted. */
export type InvitationOffer = { id: string; collection: CollectionRef | null; role: 'viewer' };

/** An invitation as its inviter sees it. */
export type Invitation = {
  id: string;
  kind: InvitationKind;
  label: string;
  invitee: PersonRef | null;
  offers: InvitationOffer[];
  maxUses: number | null;
  useCount: number;
  expiresAt: string | null;
  status: InvitationStatus;
  acceptedBy: PersonRef[];
  createdAt: string;
};

export type CreatedInvitation = { invitation: Invitation; link: string };

export type InvitationLink = { id: string; link: string; createdAt: string };

/** Signed in, the whole person; signed out, just enough to recognise them. */
export type Inviter =
  | { kind: 'person'; person: PersonRef }
  | { kind: 'initialOnly'; firstName: string; lastInitial: string };

export type PreviewOfferStatus = 'alreadyFollowing' | 'available';

export type PreviewOffer = {
  offerId: string;
  collectionName: string;
  status: PreviewOfferStatus;
  checked: boolean;
};

/**
 * What a link offers me. The screen doesn't decide the rules: available
 * offers start `checked`, `nothingNew` replaces Accept, and with `connects`
 * false Accept needs at least one available offer checked.
 */
export type InvitePreview = {
  signedIn: boolean;
  inviter: Inviter;
  connects: boolean;
  nothingNew: boolean;
  /** Only a personal invite addressed to me, signed in, can be declined. */
  canDecline: boolean;
  offers: PreviewOffer[];
};

/** A personal invite waiting for me, opened by its id rather than a link's token. */
export type ReceivedInvitation = InvitePreview & { invitationId: string };

/** Where an invitation screen reads from: a link I opened, or an invite waiting for me. */
export type InvitationSource =
  { kind: 'link'; token: string } | { kind: 'received'; invitationId: string };

/** `default` leaves it to the server (20 uses, 30 days). */
export type UsesLimit =
  { kind: 'default' } | { kind: 'unlimited' } | { kind: 'count'; count: number };
/** `at` is an ISO date-time. */
export type ExpiryLimit = { kind: 'default' } | { kind: 'never' } | { kind: 'at'; at: string };
export type GroupLinkLimits = { maxUses: UsesLimit; expiry: ExpiryLimit };
export const DEFAULT_LIMITS: GroupLinkLimits = {
  maxUses: { kind: 'default' },
  expiry: { kind: 'default' },
};

export type AcceptResult = { inviter: PersonRef; connected: boolean; joined: CollectionRef[] };

export type MatchedContact = { kind: 'email' | 'phone'; value: string };
